import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAPI } from '../../hooks/useAPI';
import { uploadGalleryFile } from '../../services/api';
import Notification from '../../components/Notification';
import ConfirmDialog from '../../components/ConfirmDialog';
import APP_CONFIG from '../../config/appConfig';
import { ProtectedRoute } from '../../utils/authGuard';

const ManageGallery = () => {
  const { data: galleryItems, loading, addItem, updateItem, deleteItem } = useAPI('gallery');
  
  // Ensure galleryItems is always an array
  const safeGalleryItems = Array.isArray(galleryItems) ? galleryItems : [];
  
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    mediaType: 'image', // 'image' or 'video'
    image: null,
    video: null,
    poster: null // For video thumbnail
  });

  const [videoDuration, setVideoDuration] = useState(0);
  const [videoError, setVideoError] = useState('');
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    item: null,
    title: 'Delete Gallery Item',
    message: 'Are you sure you want to delete this gallery item? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel'
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    setFormData({
      ...formData,
      image: e.target.files[0]
    });
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 500MB)
    if (file.size > 500 * 1024 * 1024) {
      setVideoError('Video file is too large. Maximum size is 500MB.');
      return;
    }

    // Create video element to check duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      const duration = video.duration;
      setVideoDuration(duration);
      
      if (duration > 30) {
        setVideoError('Video duration must be 30 seconds or less.');
        return;
      }
      
      setVideoError('');
      setFormData({
        ...formData,
        video: file
      });
    };

    video.onerror = () => {
      setVideoError('Invalid video file.');
    };

    video.src = URL.createObjectURL(file);
  };

  const handlePosterChange = (e) => {
    setFormData({
      ...formData,
      poster: e.target.files[0]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.mediaType === 'video' && videoError) {
      return; // Don't submit if there's a video error
    }

    setIsSubmitting(true);

    try {
      let imageUrl = '';
      let videoUrl = '';
      let posterUrl = '';

      if (formData.image) {
        const imageResponse = await uploadGalleryFile(formData.image, 'gallery');
        imageUrl = imageResponse.data.url;
      }
      if (formData.video) {
        const videoResponse = await uploadGalleryFile(formData.video, 'gallery');
        videoUrl = videoResponse.data.url;
      }
      if (formData.poster) {
        const posterResponse = await uploadGalleryFile(formData.poster, 'gallery');
        posterUrl = posterResponse.data.url;
      }

      let finalMediaUrl = '';
      let finalThumbnail = '';
      
      if (editingItem) {
        if (formData.mediaType === 'image') {
          finalMediaUrl = imageUrl || editingItem.mediaUrl || '';
          finalThumbnail = imageUrl || editingItem.thumbnail || '';
        } else {
          finalMediaUrl = videoUrl || editingItem.mediaUrl || '';
          finalThumbnail = posterUrl || editingItem.thumbnail || '';
        }
      } else {
        finalMediaUrl = formData.mediaType === 'image' ? imageUrl : videoUrl;
        finalThumbnail = formData.mediaType === 'video' ? posterUrl : imageUrl;
      }

      const itemData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        mediaType: formData.mediaType,
        mediaUrl: finalMediaUrl,
        thumbnail: finalThumbnail,
        duration: formData.mediaType === 'video' ? videoDuration : 0,
        featured: false,
        published: true,
        tags: [],
        views: 0,
        likes: 0
      };

      if (editingItem) {
        const itemId = editingItem._id || editingItem.id;
        await updateItem(itemId, itemData);
        setNotification({ message: 'Gallery item updated successfully!', type: 'success' });
      } else {
        const newItemId = await addItem(itemData);
        setNotification({ message: 'Gallery item created successfully!', type: 'success' });
      }

      setFormData({ 
        title: '', 
        description: '', 
        category: '', 
        mediaType: 'image',
        image: null, 
        video: null, 
        poster: null 
      });
      setVideoDuration(0);
      setVideoError('');
      setShowForm(false);
      setEditingItem(null);
    } catch (error) {
      setNotification({ message: 'Failed to save gallery item. Please try again.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      category: item.category,
      mediaType: item.mediaType || (item.mediaType === 'video' ? 'video' : 'image'),
      image: null, // New file upload (if any)
      video: null, // New file upload (if any)
      poster: null // New file upload (if any)
    });
    setVideoDuration(item.duration || 0);
    setVideoError('');
    setIsSubmitting(false);
    setShowForm(true);
  };

  const handleDelete = (item) => {
    setConfirmDialog({
      ...confirmDialog,
      isOpen: true,
      item: item,
      title: 'Delete Gallery Item',
      message: `Are you sure you want to delete "${item.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
  };

  const confirmDelete = async () => {
    try {
      const itemId = confirmDialog.item._id || confirmDialog.item.id;
      
      await deleteItem(itemId);
      setNotification({ message: 'Gallery item deleted successfully!', type: 'success' });
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    } catch (error) {
      setNotification({ message: 'Failed to delete gallery item. Please try again.', type: 'error' });
    }
  };

  const cancelDelete = () => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  // Get base server URL from config
  const baseServerUrl = APP_CONFIG.getBaseServerUrl();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Manage Gallery</h1>
              <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">Upload and manage your visual content</p>
            </div>
            <button
              onClick={() => {
                setShowForm(true);
                setIsSubmitting(false);
              }}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base whitespace-nowrap"
            >
              Add New Item
            </button>
          </div>

          {/* Form */}
          {showForm && (
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-black mb-3 sm:mb-4">
                {editingItem ? 'Edit Gallery Item' : 'Add New Gallery Item'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">Title</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-black"
                      placeholder="Enter gallery item title..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">Category</label>
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-black"
                      placeholder="Enter category..."
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-black mb-2">Media Type</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="mediaType"
                        value="image"
                        checked={formData.mediaType === 'image'}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <span className="text-sm text-black">Image</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="mediaType"
                        value="video"
                        checked={formData.mediaType === 'video'}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <span className="text-sm text-black">Video (Max 30 seconds)</span>
                    </label>
                  </div>
                </div>
{/* Description field commented out
                <div>
                  <label className="block text-sm font-medium text-black mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-black"
                    placeholder="Describe your gallery item..."
                  />
                </div>
*/}

                {/* Image Upload */}
                {formData.mediaType === 'image' && (
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      required={!editingItem}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-black"
                    />
                    {formData.image && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 mb-2">Preview:</p>
                        <img
                          src={URL.createObjectURL(formData.image)}
                          alt="Preview"
                          className="w-32 h-32 object-cover rounded-lg border"
                        />
                      </div>
                    )}
                    {editingItem && editingItem.mediaUrl && !formData.image && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 mb-2">Current Image:</p>
                        <img
                          src={editingItem.mediaUrl.startsWith('http') ? editingItem.mediaUrl : `${baseServerUrl}${editingItem.mediaUrl}`}
                          alt="Current"
                          className="w-32 h-32 object-cover rounded-lg border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-32 h-32 bg-gray-200 rounded-lg border flex items-center justify-center" style={{ display: 'none' }}>
                          <span className="text-gray-500 text-xs">Image not available</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Video Upload */}
                {formData.mediaType === 'video' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-black mb-2">Video File</label>
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleVideoChange}
                        required={!editingItem}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-black"
                      />
                      {videoError && (
                        <p className="text-red-500 text-sm mt-1">{videoError}</p>
                      )}
                      {videoDuration > 0 && !videoError && (
                        <p className="text-green-600 text-sm mt-1">
                          Duration: {videoDuration.toFixed(1)} seconds
                        </p>
                      )}
                    </div>
                    
                    {/* Video Thumbnail section commented out
                    <div>
                      <label className="block text-sm font-medium text-black mb-2">Video Thumbnail (Optional)</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePosterChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-black"
                      />
                      <p className="text-gray-500 text-xs mt-1">
                        If not provided, a frame from the video will be used as thumbnail
                      </p>
                    </div>
                    */}

                    {formData.video && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 mb-2">Video Preview:</p>
                        <video
                          src={URL.createObjectURL(formData.video)}
                          controls
                          className="w-64 h-36 object-cover rounded-lg border"
                        />
                      </div>
                    )}
                    {editingItem && editingItem.mediaUrl && !formData.video && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 mb-2">Current Video:</p>
                        <video
                          src={editingItem.mediaUrl.startsWith('http') ? editingItem.mediaUrl : `${baseServerUrl}${editingItem.mediaUrl}`}
                          poster={editingItem.thumbnail ? (editingItem.thumbnail.startsWith('http') ? editingItem.thumbnail : `${baseServerUrl}${editingItem.thumbnail}`) : ''}
                          controls
                          className="w-64 h-36 object-cover rounded-lg border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-64 h-36 bg-gray-200 rounded-lg border flex items-center justify-center" style={{ display: 'none' }}>
                          <span className="text-gray-500 text-xs">Video not available</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primary-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {isSubmitting 
                      ? (editingItem ? 'Updating...' : 'Creating...') 
                      : `${editingItem ? 'Update' : 'Create'} Item`}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingItem(null);
                      setFormData({ title: '', description: '', category: '', image: null });
                      setIsSubmitting(false);
                    }}
                    className="bg-gray-500 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Gallery Items Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {safeGalleryItems.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="aspect-w-16 aspect-h-9 relative">
                  {item.mediaType === 'video' ? (
                    <video
                      src={item.mediaUrl ? (item.mediaUrl.startsWith('http') ? item.mediaUrl : `${baseServerUrl}${item.mediaUrl}`) : ''}
                      poster={item.thumbnail ? (item.thumbnail.startsWith('http') ? item.thumbnail : `${baseServerUrl}${item.thumbnail}`) : ''}
                      className="w-full h-40 sm:h-48 object-cover"
                      muted
                      loop
                      playsInline
                      controls
                    />
                  ) : (
                    <img
                      src={item.mediaUrl ? (item.mediaUrl.startsWith('http') ? item.mediaUrl : `${baseServerUrl}${item.mediaUrl}`) : ''}
                      alt={item.title}
                      className="w-full h-40 sm:h-48 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  )}
                  
                  {/* Fallback for missing media */}
                  <div className="w-full h-40 sm:h-48 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center" style={{ display: 'none' }}>
                    <div className="text-center text-gray-500">
                      <svg className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      <p className="text-xs sm:text-sm font-medium">No Media</p>
                    </div>
                  </div>
                  
                  {/* Media Type Badge */}
                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.mediaType === 'video'
                        ? 'bg-red-500 text-white' 
                        : 'bg-blue-500 text-white'
                    }`}>
                      {item.mediaType === 'video' ? 'Video' : 'Image'}
                    </span>
                  </div>
                </div>
                <div className="p-3 sm:p-4">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{item.title}</h4>
{/* <p className="text-gray-600 text-sm mt-1 line-clamp-2">{item.description}</p> */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-3 gap-2">
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs self-start">
                      {item.category}
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="text-red-600 hover:text-red-700 text-xs sm:text-sm hover:bg-red-50 px-2 py-1 rounded transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {galleryItems.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-500">
                No gallery items yet. Upload your first image!
              </div>
            )}
          </div>

          <div className="mt-6 sm:mt-8">
            <Link
                to="/admin/dashboard"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
      
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />
    </>
  );
};

const ProtectedManageGallery = () => (
  <ProtectedRoute>
    <ManageGallery />
  </ProtectedRoute>
);

export default ProtectedManageGallery;
